import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Link2, Mail, Pencil, Phone, Plus, Unlink, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { CustomerStatusBadge } from '../../components/customers/CustomerStatusBadge';
import { OpportunityStageBadge } from '../../components/sales/OpportunityStageBadge';
import { OpportunityFormDialog } from '../../components/sales/OpportunityFormDialog';
import { StageProposalCard } from '../../components/sales/StageProposalCard';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { unwrapArray } from '../../lib/api-envelope';
import { formatCalendarDate, formatInstant, parseInstantMs } from '../../lib/calendar-date';
import { CUSTOMER_STATUS_LABELS, isCustomerStatus } from '../../lib/customer-status';
import { fmtCurrency } from '../../lib/display-labels';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canChangeOpportunity,
  canCreateJob,
  canCreateQuote,
  daysInStage,
  dealWeightedValue,
  leadSourceLabel,
  LOST_REASON_LABELS,
  lostReasonLabel,
  nextStepOverdue,
  STAGE_LABELS,
} from '../../lib/opportunity';
import { useVerticalLabel } from '../../hooks/useVerticalCatalog';
import { salesActivityKindLabel } from '../../lib/sales-activity';
import {
  LOST_REASONS,
  OPPORTUNITY_STAGES,
  type LostReason,
  type OpportunityDetail as OpportunityDetailRow,
  type OpportunityLinkResult,
  type OpportunityStage,
  type OpportunityWriteResult,
} from '../../types/opportunity';

/**
 * /sales/pipeline/:opportunityId (CRM-FE-3).
 *
 * STAGE MOVES are PATCH /opportunities/:id with `stage`.
 *   · LOST needs a reason IN THE SAME REQUEST. The modal will not send without
 *     one, and the API's own 400 is shown if it refuses anyway.
 *   · WON promotes a prospect or inactive customer to active, in the same
 *     transaction. The confirm says so before, and the API's
 *     `customerPromotedFrom` is what the notice after reports.
 * LINKING A QUOTE never moves the stage (D16): the API answers
 * `suggestedStage: 'quoted'` and the page OFFERS that move; the person decides.
 *
 * Quote/job pickers offer only THIS customer's records — the API refuses
 * anything else with "That quote is for a different customer".
 *
 * LOST REASON is a CODE (lost_reason ∈ LOST_REASONS, 0542); "Other" also
 * needs a note (lost_reason_note) — the brief's rule, enforced here; the API
 * requires only the code.
 *
 * WEIGHTED VALUE is one deal's value × its effective probability
 * (lib/opportunity dealWeightedValue — the API sends no per-deal figure). An
 * unestimated deal shows `—`, never $0.
 *
 * ACTIVITY is the meetings, stage changes and linked quotes the API returned,
 * interleaved newest first — a reordering, nothing computed. Days in stage
 * counts tenant calendar days since the entry into the current stage.
 *
 * Panels stack full width (record-page rule).
 */
type TimelineEntry = { key: string; kind: 'Meeting' | 'Stage' | 'Quote'; at: number; when: string; node: React.ReactNode };

const inputClass =
  'w-full rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-t border-fieldiq-border py-3 first:border-t-0 sm:flex-row sm:justify-between">
      <span className="text-sm text-fieldiq-muted">{label}</span>
      <span className="text-sm text-white sm:max-w-[65%] sm:text-right">{children}</span>
    </div>
  );
}

function Figure({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="min-w-0" data-testid={`figure-${label}`}>
      <dt className="text-xs font-semibold text-fieldiq-muted">{label}</dt>
      <dd className="mt-0.5 text-lg font-extrabold tabular-nums text-white">{value}</dd>
      {sub && <dd className="text-xs text-fieldiq-muted">{sub}</dd>}
    </div>
  );
}

function Panel({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs font-black uppercase tracking-[0.14em] text-fieldiq-muted">{title}</h2>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export default function OpportunityDetail() {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  const verticalLabel = useVerticalLabel();

  const [editing, setEditing] = useState(false);
  const [pendingStage, setPendingStage] = useState<OpportunityStage | null>(null);
  const [lostReason, setLostReason] = useState<LostReason | ''>('');
  const [lostNote, setLostNote] = useState('');
  const [stageError, setStageError] = useState<string | null>(null);
  const [promotion, setPromotion] = useState<string | null>(null);
  // Arriving from "New quote", the link's own suggestion rides in the
  // navigation state; it is offered the same way as an in-page link.
  const location = useLocation();
  const [suggestQuoted, setSuggestQuoted] = useState(
    (location.state as { suggestedStage?: string | null } | null)?.suggestedStage === 'quoted',
  );
  const [linkQuoteId, setLinkQuoteId] = useState('');
  const [linkJobId, setLinkJobId] = useState('');

  const query = useQuery({
    queryKey: ['opportunity', opportunityId],
    queryFn: () => api.opportunities.get(opportunityId!).then((r) => r.data.data as OpportunityDetailRow),
    enabled: !!opportunityId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const o = failure ? undefined : query.data;
  const canChange = canChangeOpportunity(user, o);

  // The contact card's phone and email: the deal carries only the contact's id and name.
  const contactsQuery = useQuery({
    queryKey: ['customer-contacts', o?.customerId],
    queryFn: () => api.customers.listContacts(o!.customerId).then((r) => unwrapArray<any>(r.data)),
    enabled: !!o?.contactId,
  });
  const contact = o?.contactId ? (contactsQuery.data ?? []).find((c) => c.id === o.contactId) : undefined;

  // Only this customer's quotes — the API's customer filter, not a client one.
  const quotesQuery = useQuery({
    queryKey: ['quotes', 'for-customer', o?.customerId],
    queryFn: () => api.quotes.list({ customerId: o!.customerId, limit: '200' }).then((r) => unwrapArray<any>(r.data)),
    enabled: !!o && canChange,
  });
  // GET /jobs has no customer filter; the picker narrows the loaded list to this
  // customer. That is a choice of rows to offer, not a figure.
  const jobsQuery = useQuery({
    queryKey: ['jobs', 'for-opportunity-picker'],
    queryFn: () => api.jobs.list().then((r) => unwrapArray<any>(r.data)),
    enabled: !!o && canChange,
  });
  const linkedQuoteIds = useMemo(() => new Set((o?.quotes ?? []).map((q) => q.id)), [o]);
  const linkedJobIds = useMemo(() => new Set((o?.jobs ?? []).map((j) => j.id)), [o]);
  const quoteOptions = (quotesQuery.data ?? []).filter((q) => (q.customer_id ?? q.customerId) === o?.customerId && !linkedQuoteIds.has(q.id));
  const jobOptions = (jobsQuery.data ?? []).filter((j) => (j.customer_id ?? j.customerId) === o?.customerId && !linkedJobIds.has(j.id));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
    queryClient.invalidateQueries({ queryKey: ['opportunities'] });
  };

  const stageMutation = useMutation({
    mutationFn: (vars: { stage: OpportunityStage; lost_reason?: LostReason; lost_reason_note?: string | null }) =>
      api.opportunities.update(opportunityId!, vars).then((r) => r.data.data as OpportunityWriteResult),
    onSuccess: (row, vars) => {
      invalidate();
      setPendingStage(null);
      setLostReason('');
      setLostNote('');
      if (vars.stage === 'quoted') setSuggestQuoted(false);
      if (row?.customerPromotedFrom) {
        queryClient.invalidateQueries({ queryKey: ['customers'] });
        setPromotion(
          `${row.customerName ?? 'The customer'} is now an active customer (was ${CUSTOMER_STATUS_LABELS[row.customerPromotedFrom].toLowerCase()}).`,
        );
      }
    },
    onError: (err) => setStageError(getApiErrorMessage(err, 'Could not change the stage.')),
  });

  const linkMutation = useMutation({
    mutationFn: (v: { kind: 'quote' | 'job'; id: string; unlink?: boolean }) => {
      const fn = v.kind === 'quote'
        ? (v.unlink ? api.opportunities.unlinkQuote : api.opportunities.linkQuote)
        : (v.unlink ? api.opportunities.unlinkJob : api.opportunities.linkJob);
      return fn(opportunityId!, v.id).then((r) => r.data.data as OpportunityLinkResult);
    },
    onSuccess: (res, v) => {
      invalidate();
      if (v.kind === 'quote') setLinkQuoteId(''); else setLinkJobId('');
      // D16: a suggestion, never a move.
      if (!v.unlink && res?.suggestedStage === 'quoted') setSuggestQuoted(true);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not change the link.')),
  });

  const requestStage = (stage: OpportunityStage) => {
    if (!o || stage === o.stage) return;
    setStageError(null);
    if (stage === 'lost' || (stage === 'won' && o.customerStatus !== 'active')) {
      setPendingStage(stage);
      return;
    }
    stageMutation.mutate({ stage });
  };

  const confirmPending = () => {
    if (!pendingStage) return;
    if (pendingStage === 'lost') {
      if (!lostReason) { setStageError('Say why it was lost.'); return; }
      if (lostReason === 'other' && !lostNote.trim()) { setStageError('Add a note saying what the other reason was.'); return; }
      stageMutation.mutate({ stage: 'lost', lost_reason: lostReason, lost_reason_note: lostNote.trim() || null });
      return;
    }
    stageMutation.mutate({ stage: pendingStage });
  };

  const overdue = o ? nextStepOverdue(o, timeZone) : null;
  const weighted = o ? dealWeightedValue(o.estimatedValue, o.probability) : null;
  const inStage = o ? daysInStage(o, timeZone) : null;

  const timeline = useMemo<TimelineEntry[]>(() => {
    if (!o) return [];
    const out: TimelineEntry[] = [];
    for (const m of o.meetings) {
      out.push({
        key: `m-${m.id}`, kind: 'Meeting', at: parseInstantMs(m.occurredAt) ?? 0, when: m.occurredAt,
        node: (
          <>
            <Link to={`/sales/meetings/${m.id}`} className="font-semibold text-white hover:underline">{salesActivityKindLabel(m.kind)}</Link>
            {m.nextStep && <p className="text-fieldiq-muted">Next: {m.nextStep}</p>}
          </>
        ),
      });
    }
    for (const h of o.stageHistory) {
      out.push({
        key: `h-${h.id}`, kind: 'Stage', at: parseInstantMs(h.changedAt) ?? 0, when: h.changedAt,
        node: (
          <span className="text-white">
            {h.fromStage ? `${STAGE_LABELS[h.fromStage as OpportunityStage] ?? '—'} → ` : 'Created as '}
            {STAGE_LABELS[h.toStage as OpportunityStage] ?? '—'}
            <span className="text-fieldiq-muted"> · {h.agentRunId ? 'approved from Scout · ' : ''}{h.changedByName ?? '—'}</span>
          </span>
        ),
      });
    }
    for (const q of o.quotes) {
      out.push({
        key: `q-${q.id}`, kind: 'Quote', at: parseInstantMs(q.createdAt) ?? 0, when: q.createdAt,
        node: (
          <Link to={`/quotes/${q.id}`} className="text-white hover:underline">
            Quote {q.quoteNumber ?? '—'} · {q.status} · {fmtCurrency(q.totalAmount, q.currency)}
          </Link>
        ),
      });
    }
    return out.sort((a, b) => b.at - a.at);
  }, [o]);

  const customerQs = o ? `customer=${encodeURIComponent(o.customerId)}&opportunity=${encodeURIComponent(o.id)}` : '';

  return (
    <div data-testid="opportunity-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Sales · Opportunity"
        title={o?.title ?? 'Opportunity'}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          o && canChange ? (
            <button type="button" onClick={() => setEditing(true)} className="fi-action fi-action--standard fi-action--primary">
              <Pencil className="size-4" aria-hidden="true" /> Edit
            </button>
          ) : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {failure ? (
            <LoadFailureNotice error={queryFailureError(query)} subject="this opportunity" />
          ) : !o ? (
            <div className="h-40 animate-pulse rounded-fi-panel bg-fieldiq-card" aria-busy="true" />
          ) : (
            <>
              {promotion && (
                <p role="status" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-3 text-sm text-white">{promotion}</p>
              )}

              {o.pendingStageProposal && (
                <StageProposalCard proposal={o.pendingStageProposal} canDecide={canChange} />
              )}

              <Panel title="Stage">
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Stage">
                  {OPPORTUNITY_STAGES.map((s) => {
                    const current = o.stage === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        aria-pressed={current}
                        disabled={!canChange || stageMutation.isPending}
                        onClick={() => requestStage(s)}
                        className={`rounded-fi-pill px-4 py-1.5 text-xs font-extrabold ${
                          current ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-dark text-fieldiq-text disabled:opacity-60'
                        }`}
                      >
                        {STAGE_LABELS[s]}
                      </button>
                    );
                  })}
                  <OpportunityStageBadge stage={o.stage} />
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                  <Figure label="Value" value={fmtCurrency(o.estimatedValue, o.currency)} />
                  <Figure
                    label="Probability"
                    value={Number.isFinite(o.probability) ? `${o.probability}%` : '—'}
                    sub={o.probabilityIsDefault ? 'Stage default' : 'Set by hand'}
                  />
                  <Figure label="Weighted value" value={fmtCurrency(weighted, o.currency)} sub={weighted === null ? 'No value estimated' : undefined} />
                  <Figure label="Days in stage" value={inStage === null ? '—' : String(inStage)} />
                </dl>
                {stageError && !pendingStage && <p role="alert" className="mt-2 text-sm text-fieldiq-text">{stageError}</p>}
                {suggestQuoted && o.stage !== 'quoted' && canChange && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-fieldiq-border bg-fieldiq-dark p-3 text-sm text-fieldiq-text">
                    A quote is linked. Move this opportunity to Quoted?
                    <button type="button" onClick={() => requestStage('quoted')} className="rounded-lg border border-fieldiq-border px-3 py-1 font-semibold text-white">Move to Quoted</button>
                    <button type="button" onClick={() => setSuggestQuoted(false)} className="px-2 py-1 text-fieldiq-muted">Not now</button>
                  </div>
                )}
              </Panel>

              <Panel title="Next step">
                {o.nextStep || o.nextStepDue ? (
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="min-w-0 text-sm text-white">{o.nextStep ?? '—'}</p>
                    <span className="inline-flex items-center gap-2 text-sm text-fieldiq-text">
                      <CalendarClock className="size-4" aria-hidden="true" />
                      Due {formatCalendarDate(o.nextStepDue) ?? '—'}
                      {overdue === true && (
                        <span data-testid="next-step-overdue" className="rounded-fi-pill border border-black/20 bg-fi-signal-red px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink">
                          Overdue
                        </span>
                      )}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-fieldiq-muted">No next step set{canChange ? ' — add one with Edit' : ''}</p>
                )}
              </Panel>

              <Panel title="Contact">
                {!o.contactId ? (
                  <p className="text-sm text-fieldiq-muted">No contact on this deal</p>
                ) : (
                  <div className="flex flex-wrap items-start gap-3">
                    <span className="flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-fieldiq-dark text-fieldiq-muted">
                      <UserRound className="size-5" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 space-y-1 text-sm">
                      <p className="font-semibold text-white">{o.contactName ?? contact?.name ?? '—'}{contact?.role ? <span className="font-normal text-fieldiq-muted"> · {contact.role}</span> : null}</p>
                      {contact?.phone && (
                        <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 text-fieldiq-text hover:underline"><Phone className="size-3.5" aria-hidden="true" />{contact.phone}</a>
                      )}
                      {contact?.email && (
                        <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 break-all text-fieldiq-text hover:underline"><Mail className="size-3.5" aria-hidden="true" />{contact.email}</a>
                      )}
                      {contact && !contact.phone && !contact.email && <p className="text-fieldiq-muted">No phone or email on file</p>}
                    </div>
                  </div>
                )}
              </Panel>

              <section className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-4 py-2">
                <Row label="Customer">
                  <span className="inline-flex items-center gap-2">
                    <Link to={`/customers/${o.customerId}`} className="font-semibold hover:underline">{o.customerName ?? '—'}</Link>
                    <CustomerStatusBadge status={o.customerStatus} />
                  </span>
                </Row>
                <Row label="Service line">{o.vertical ? verticalLabel(o.vertical) : '—'}</Row>
                <Row label="Expected close">{formatCalendarDate(o.expectedCloseDate) ?? '—'}</Row>
                <Row label="Expected start">{formatCalendarDate(o.expectedStart) ?? '—'}</Row>
                <Row label="Owner">{o.ownerName ?? '—'}</Row>
                <Row label="Lead source">{leadSourceLabel(o.leadSource)}</Row>
                <Row label="Well">{o.wellName ?? '—'}</Row>
                <Row label="Pad / LSD">{o.padOrLsd ?? '—'}</Row>
                <Row label="Rig">{o.rigName ?? '—'}</Row>
                <Row label="Area">{o.area ?? '—'}</Row>
                <Row label="Competitor">{o.competitor ?? '—'}</Row>
                {o.stage === 'lost' && (
                  <Row label="Lost because">
                    {lostReasonLabel(o.lostReason) ?? '—'}{o.lostReasonNote ? ` — ${o.lostReasonNote}` : ''}
                  </Row>
                )}
                <Row label="Notes">{o.notes ?? '—'}</Row>
              </section>

              <Panel
                title="Quotes"
                action={canCreateQuote(user?.role) ? (
                  <Link to={`/quotes/create?${customerQs}`} className="inline-flex items-center gap-1 text-sm font-semibold text-fieldiq-accent-signal">
                    <Plus size={14} aria-hidden="true" /> New quote
                  </Link>
                ) : undefined}
              >
                {o.quotes.length === 0 ? (
                  <p className="text-sm text-fieldiq-muted">No quotes linked</p>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {o.quotes.map((q) => (
                      <li key={q.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                        <Link to={`/quotes/${q.id}`} className="text-white hover:underline">
                          {[q.quoteNumber ?? '—', q.status, fmtCurrency(q.totalAmount, q.currency)].join(' · ')}
                        </Link>
                        {canChange && (
                          <button type="button" aria-label={`Unlink ${q.quoteNumber ?? 'quote'}`} onClick={() => linkMutation.mutate({ kind: 'quote', id: q.id, unlink: true })} className="text-fieldiq-muted hover:text-white">
                            <Unlink size={14} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {canChange && (
                  <div className="mt-3 flex gap-2">
                    <select aria-label="Link a quote" value={linkQuoteId} onChange={(e) => setLinkQuoteId(e.target.value)} className={inputClass}>
                      <option value="">{quotesQuery.isLoading ? 'Loading quotes…' : quoteOptions.length ? 'Link an existing quote…' : 'No other quotes for this customer'}</option>
                      {quoteOptions.map((q) => (
                        <option key={q.id} value={q.id}>{[q.quote_number ?? q.quoteNumber ?? '—', q.status].join(' · ')}</option>
                      ))}
                    </select>
                    <button type="button" aria-label="Link quote" disabled={!linkQuoteId || linkMutation.isPending} onClick={() => linkMutation.mutate({ kind: 'quote', id: linkQuoteId })} className="inline-flex items-center gap-1 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 text-sm font-semibold text-white disabled:opacity-50">
                      <Link2 size={14} aria-hidden="true" /> Link
                    </button>
                  </div>
                )}
              </Panel>

              <Panel
                title="Jobs"
                action={canCreateJob(user?.role) ? (
                  <Link to={`/jobs/new?${customerQs}`} className="inline-flex items-center gap-1 text-sm font-semibold text-fieldiq-accent-signal">
                    <Plus size={14} aria-hidden="true" /> Create job
                  </Link>
                ) : undefined}
              >
                {o.jobs.length === 0 ? (
                  <p className="text-sm text-fieldiq-muted">No jobs linked</p>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {o.jobs.map((j) => (
                      <li key={j.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                        <Link to={`/jobs/${j.id}`} className="text-white hover:underline">
                          {[j.jobNumber ?? '—', j.status, formatCalendarDate(j.scheduledDate) ?? '—'].join(' · ')}
                        </Link>
                        {canChange && (
                          <button type="button" aria-label={`Unlink ${j.jobNumber ?? 'job'}`} onClick={() => linkMutation.mutate({ kind: 'job', id: j.id, unlink: true })} className="text-fieldiq-muted hover:text-white">
                            <Unlink size={14} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {canChange && (
                  <div className="mt-3 flex gap-2">
                    <select aria-label="Link a job" value={linkJobId} onChange={(e) => setLinkJobId(e.target.value)} className={inputClass}>
                      <option value="">{jobsQuery.isLoading ? 'Loading jobs…' : jobOptions.length ? 'Link an existing job…' : 'No other jobs for this customer'}</option>
                      {jobOptions.map((j) => (
                        <option key={j.id} value={j.id}>{[j.job_number ?? j.jobNumber ?? '—', j.status].join(' · ')}</option>
                      ))}
                    </select>
                    <button type="button" aria-label="Link job" disabled={!linkJobId || linkMutation.isPending} onClick={() => linkMutation.mutate({ kind: 'job', id: linkJobId })} className="inline-flex items-center gap-1 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 text-sm font-semibold text-white disabled:opacity-50">
                      <Link2 size={14} aria-hidden="true" /> Link
                    </button>
                  </div>
                )}
              </Panel>

              <Panel
                title="Activity"
                action={
                  <Link to={`/sales/meetings/new?${customerQs}`} className="inline-flex items-center gap-1 text-sm font-semibold text-fieldiq-accent-signal">
                    <Plus size={14} aria-hidden="true" /> Log meeting
                  </Link>
                }
              >
                {timeline.length === 0 ? (
                  <p className="text-sm text-fieldiq-muted">No meetings, stage changes or quotes recorded</p>
                ) : (
                  <ol className="relative space-y-3 border-l border-fieldiq-border pl-4" aria-label="Activity">
                    {timeline.map((t) => (
                      <li key={t.key} className="text-sm">
                        <span aria-hidden="true" className="absolute -left-[5px] mt-1.5 size-2.5 rounded-full border border-fieldiq-border bg-fieldiq-card" />
                        <p className="text-xs text-fieldiq-muted">
                          <span className="font-bold uppercase tracking-wider">{t.kind}</span> · {formatInstant(t.when, timeZone) ?? '—'}
                        </p>
                        <div className="mt-0.5">{t.node}</div>
                      </li>
                    ))}
                  </ol>
                )}
              </Panel>
            </>
          )}
        </div>
      </section>

      <ConfirmModal
        isOpen={pendingStage !== null}
        title={pendingStage === 'lost' ? 'Mark lost' : 'Mark won'}
        confirmLabel={pendingStage === 'lost' ? 'Mark lost' : 'Mark won'}
        confirmVariant="primary"
        loading={stageMutation.isPending}
        onCancel={() => { setPendingStage(null); setStageError(null); setLostReason(''); setLostNote(''); }}
        onConfirm={confirmPending}
        body={
          pendingStage === 'lost' ? (
            <div className="space-y-2">
              <label htmlFor="lost-reason" className="block text-sm text-white">Why was it lost? *</label>
              <select id="lost-reason" value={lostReason} onChange={(e) => { setLostReason(e.target.value as LostReason | ''); setStageError(null); }} className={inputClass}>
                <option value="">Pick a reason</option>
                {LOST_REASONS.map((r) => <option key={r} value={r}>{LOST_REASON_LABELS[r]}</option>)}
              </select>
              <label htmlFor="lost-note" className="block text-sm text-white">Note{lostReason === 'other' ? ' *' : ''}</label>
              <textarea
                id="lost-note"
                rows={3}
                value={lostNote}
                onChange={(e) => setLostNote(e.target.value)}
                className={inputClass}
                placeholder={lostReason === 'other' ? 'What was the reason?' : 'Optional'}
              />
              {stageError && <p role="alert" className="text-sm text-fieldiq-text">{stageError}</p>}
            </div>
          ) : (
            <div className="space-y-2">
              <p>
                Marking this won makes {o?.customerName ?? 'the customer'} an <strong>active</strong> customer
                {o && isCustomerStatus(o.customerStatus) ? ` (it is ${CUSTOMER_STATUS_LABELS[o.customerStatus].toLowerCase()} now)` : ''}.
              </p>
              {stageError && <p role="alert" className="text-sm text-fieldiq-text">{stageError}</p>}
            </div>
          )
        }
      />

      <OpportunityFormDialog open={editing} existing={o ?? null} onClose={() => setEditing(false)} />
    </div>
  );
}
